import React from 'react';
import { Card, Heading, Text } from '@radix-ui/themes';
import MaterialIcon from '../common/MaterialIcon';

type WeightHomeHighlight = {
  memberName: string;
  memberImageUrl: string;
  afterYear: number;
  previousYear: number;
  beforeWeight: number;
  afterWeight: number;
  difference: number;
};

type WeightHomeGainHighlight = {
  memberName: string;
  memberImageUrl: string;
  year: number;
  beforeWeight: number;
  afterWeight: number;
  difference: number;
};

type HomeTabProps = {
  isAdmin: boolean;
  userEmail: string;
  weightHighlight: WeightHomeHighlight | null;
  weightGainHighlight: WeightHomeGainHighlight | null;
};

const CONFETTI_COLORS = ['#f59e0b', '#f97316', '#ef4444', '#84cc16', '#22c55e', '#0ea5e9', '#ec4899'];

const GRAND_WINNER_CONFETTI = Array.from({ length: 28 }, (_, index) => ({
  left: `${(index * 7.5) % 100}%`,
  delay: `${(index % 8) * 0.32}s`,
  duration: `${3 + (index % 5) * 0.45}s`,
  color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
}));

const RISING_STAR_CONFETTI = Array.from({ length: 18 }, (_, index) => ({
  left: `${(index * 9.8) % 100}%`,
  delay: `${(index % 6) * 0.38}s`,
  duration: `${3.3 + (index % 4) * 0.5}s`,
  color: CONFETTI_COLORS[(index + 2) % CONFETTI_COLORS.length],
}));

function HomeTab({ isAdmin, userEmail, weightHighlight, weightGainHighlight }: HomeTabProps) {
  return (
    <>
      <Heading size="8" className="font-display text-pine-900">
        <span className="inline-flex items-center gap-2">
          <MaterialIcon name="celebration" filled className="text-[36px] text-amber-500" />
          Velkommen til De Fede Svin
        </span>
      </Heading>
      <Text size="2" color="gray" className="mt-2 block">
        {userEmail}
        {isAdmin ? ' · Admin med fuld adgang' : ''}
      </Text>
      <br />
      <Heading size="7" className="font-display text-pine-900">
        <span className="inline-flex items-center gap-2">
          <MaterialIcon name="emoji_events" filled className="text-[30px] text-amber-600" />
          Årets vindere
        </span>
      </Heading>
      {weightHighlight && (
        <Card className="winner-card mt-5 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-rose-50 to-orange-100 p-4 shadow-sm">
          <div className="winner-party-layer" aria-hidden>
            {GRAND_WINNER_CONFETTI.map((piece, index) => (
              <span
                key={`grand-confetti-${piece.left}-${piece.delay}`}
                className="winner-confetti-piece"
                style={{
                  left: piece.left,
                  animationDelay: piece.delay,
                  animationDuration: piece.duration,
                  backgroundColor: piece.color,
                  transform: `rotate(${(index % 6) * 12}deg)`,
                }}
              />
            ))}
            <span className="winner-balloon winner-balloon-a" />
            <span className="winner-balloon winner-balloon-b" />
            <span className="winner-balloon winner-balloon-c" />
            <span className="winner-sparkle winner-sparkle-left">
              <MaterialIcon name="auto_awesome" filled className="text-[22px]" />
            </span>
            <span className="winner-sparkle winner-sparkle-right">
              <MaterialIcon name="stars" filled className="text-[20px]" />
            </span>
          </div>

          <div className="winner-content grid gap-4 sm:grid-cols-[112px_1fr] sm:items-center">
            <div className="relative h-28 w-28 overflow-hidden rounded-2xl border border-amber-200 bg-slate-100 shadow-md">
              <span className="winner-crown" aria-hidden>
                <MaterialIcon name="crown" filled className="text-[34px] text-amber-500" />
              </span>
              {weightHighlight.memberImageUrl ? (
                <img
                  src={weightHighlight.memberImageUrl}
                  alt={weightHighlight.memberName}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : null}
            </div>

            <div>
              <Text size="5" weight="bold" className="uppercase tracking-[0.22em] text-amber-700">
                <span className="inline-flex items-center gap-2">
                  <MaterialIcon name="workspace_premium" filled className="text-[20px]" />
                  Årets Fede Svin
                </span>
              </Text>
              <Heading size="7" className="mt-2 font-display text-pine-900">
                {weightHighlight.memberName}
              </Heading>
              <Text size="2" className="mt-2 block text-slate-700">
                {weightHighlight.afterYear} efter Julefrokost: {weightHighlight.afterWeight.toFixed(1)} kg
                {' '}
                · {weightHighlight.previousYear} før Julefrokost: {weightHighlight.beforeWeight.toFixed(1)} kg
              </Text>
              <Text size="2" weight="medium" className="mt-2 block text-pine-800">
                <span className="inline-flex items-center gap-1">
                  <MaterialIcon
                    name={weightHighlight.difference >= 0 ? 'trending_up' : 'trending_down'}
                    className="text-[18px]"
                  />
                  {weightHighlight.difference >= 0 ? '+' : ''}
                  {weightHighlight.difference.toFixed(1)} kg siden sidste år
                </span>
              </Text>
              <Text size="1" className="mt-2 block uppercase tracking-[0.12em] text-amber-700">
                Konfetti, krone og total legende-status.
              </Text>
            </div>
          </div>
        </Card>
      )}

      {weightGainHighlight && (
        <Card className="winner-card mt-5 rounded-2xl border border-pine-200 bg-gradient-to-br from-white via-pine-50 to-emerald-100 p-4 shadow-sm">
          <div className="winner-party-layer" aria-hidden>
            {RISING_STAR_CONFETTI.map((piece, index) => (
              <span
                key={`rise-confetti-${piece.left}-${piece.delay}`}
                className="winner-confetti-piece"
                style={{
                  left: piece.left,
                  animationDelay: piece.delay,
                  animationDuration: piece.duration,
                  backgroundColor: piece.color,
                  transform: `rotate(${(index % 4) * 16}deg)`,
                }}
              />
            ))}
            <span className="winner-sparkle winner-sparkle-left">
              <MaterialIcon name="whatshot" filled className="text-[22px] text-rose-500" />
            </span>
            <span className="winner-sparkle winner-sparkle-right">
              <MaterialIcon name="rocket_launch" filled className="text-[20px] text-pine-700" />
            </span>
          </div>

          <div className="winner-content grid gap-4 sm:grid-cols-[112px_1fr] sm:items-center">
            <div className="h-28 w-28 overflow-hidden rounded-2xl border border-pine-100 bg-slate-100">
              {weightGainHighlight.memberImageUrl ? (
                <img
                  src={weightGainHighlight.memberImageUrl}
                  alt={weightGainHighlight.memberName}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              ) : null}
            </div>

            <div>
              <Text size="5" weight="bold" className="uppercase tracking-[0.22em] text-pine-600">
                <span className="inline-flex items-center gap-2">
                  <MaterialIcon name="pets" className="text-[20px]" />
                  Årets Grisling
                </span>
              </Text>
              <Heading size="7" className="mt-2 font-display text-pine-900">
                {weightGainHighlight.memberName}
              </Heading>
              <Text size="2" className="mt-2 block text-slate-700">
                {weightGainHighlight.year} efter Julefrokost: {weightGainHighlight.afterWeight.toFixed(1)} kg
                {' '}
                · {weightGainHighlight.year} før Julefrokost: {weightGainHighlight.beforeWeight.toFixed(1)} kg
              </Text>
              <Text size="2" weight="medium" className="mt-2 block text-pine-800">
                <span className="inline-flex items-center gap-1">
                  <MaterialIcon name="bolt" className="text-[18px]" />
                  +{weightGainHighlight.difference.toFixed(1)} kg mellem før og efter Julefrokost
                </span>
              </Text>
              <Text size="1" className="mt-2 block uppercase tracking-[0.12em] text-pine-700">
                Hurtigste julefrokost-acceleration i feltet.
              </Text>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}

export default HomeTab;
